import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Zana, { ZanaState } from './Zana';
import { askZana, AskPageContext, AskTurn, warmZanaContext, fetchZanaBriefing, fetchZanaGuide, fetchZanaSync } from '../services/briefingsApi';
import {
  ZanaIntent, ZanaIntentExecutor, isExecutableIntent, intentNeedsConfirm,
  labelForZanaIntent,
} from '../services/zanaActions';
import { shapeAskResponse, isAffirmation, isDenial, isGuideInterrupt, restingFooter,
  HeldConfirm, armHeldConfirm, heldConfirmIsLive, threadMovedUnder, newZanaMsgId, staleHeldConfirmReply,
  BRANCH_SWITCHED_REPLY, chipMayRun, wantsGuidedWalk,
} from '../services/zanaAskShared';
import { auth } from '../firebase';
import {
  ZanaChatMsg,
  ZanaConversation,
  loadActiveConversation,
  saveActiveConversation,
  startNewConversation,
  listConversationHistory,
  restoreConversation,
  deleteHistoryConversation,
  ZANA_CHAT_IDLE_MS,
  archiveActiveConversation,
  subscribeZanaChat,
  appendZanaNotice,
} from '../services/zanaChatStore';
import { episodesFromChat } from '../services/zanaEpisodes';
import {
  ZANA_CHAT_STYLES,
  ZanaContextBar,
  ZanaMessageBubble,
  ZanaThinkingBubble,
  ZanaMsgSource,
} from './ZanaChatUi';

/** Wall-clock for a reset instant; mirrors the dock's formatReset. */
function formatResetAt(ms?: number): string {
  if (!ms || !Number.isFinite(ms)) return 'shortly';
  const now = Date.now();
  if (ms <= now) return 'now';
  if (ms - now > 14 * 3600_000) return 'tomorrow';
  try {
    return new Date(ms).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  } catch {
    return 'shortly';
  }
}

/* ============================================================================
   ZanaChatPage — Zana with room to think.

   The dock is a corner pill: good for "open fuel", bad for the conversation an
   owner actually wants ("which bus do I retire, and show me the numbers").
   Short answers were partly a UI problem — nobody writes six sentences of
   reasoning into a 320px box, and nobody reads them there either.

   Same brain, same endpoint, same allow-listed intents as the dock. What is
   different here is the room: full-width transcript, multi-line composer, and
   starter questions that are decisions rather than lookups — the questions that
   route to Zana AI instead of being answered from the cards on screen.

   Conversation persists across tab switches (sessionStorage), archives to
   History on New conversation / 1h idle, and clears on logout or site close.
   ========================================================================== */

type ChatMsg = ZanaChatMsg;

/** Decisions, not lookups. Every one of these reaches the reasoning path — the
 *  point of the page is to show owners what she is actually for. */
const STARTERS: { label: string; q: string }[] = [
  { label: 'Where is my money leaking?', q: 'Where is my money leaking this month, and what is the single biggest line?' },
  { label: 'Which bus should I retire?', q: 'Which bus should I retire first, and what evidence supports it?' },
  { label: 'How do I earn more?', q: 'How do I get more revenue out of the buses I already have?' },
  { label: 'Cut cost, keep routes', q: 'Help me cut cost without cutting any routes.' },
  { label: 'What is driving fuel up?', q: 'What is driving my fuel spend up, bus by bus?' },
  { label: 'What should I do first?', q: 'If I only fix one thing this week, what should it be and why?' },
];

const PANEL = '#1a1430';
const PANEL_SOFT = 'rgba(255,255,255,.05)';
const EDGE = 'rgba(155,124,255,.28)';

function formatWhen(ms: number): string {
  const d = new Date(ms);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (sameDay) return time;
  return `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })} · ${time}`;
}

function seedActiveThread(org: string | null, user: string | null): { messages: ChatMsg[]; id: string | null } {
  if (!org || !user) return { messages: [], id: null };
  const active = loadActiveConversation(org, user);
  return { messages: (active?.messages || []) as ChatMsg[], id: active?.id || null };
}

export default function ZanaChatPage({
  orgCode,
  pageContext,
  liveCounts,
  onExecuteIntent,
  liveUnreadAlertCount = 0,
  liveHighRiskBusCount = 0,
  takeoverActive = false,
  onStopGuide,
}: {
  orgCode: string | null;
  /** The same screen context the dock sends: page, cards, visible rows. */
  pageContext?: AskPageContext;
  /** Live figures from the open console — see ZanaDock (2026-09-29). */
  liveCounts?: Record<string, unknown>;
  onExecuteIntent?: ZanaIntentExecutor;
  liveUnreadAlertCount?: number;
  liveHighRiskBusCount?: number;
  /** True while a Guide Mode / takeover walk is on screen, in any phase. */
  takeoverActive?: boolean;
  /** Interrupt yields Guide Mode back to chat ("stop" / "enough"). */
  onStopGuide?: () => void;
}) {
  const uid = auth.currentUser?.uid || null;
  const seeded = seedActiveThread(orgCode, uid);
  const [chat, setChat] = useState<ChatMsg[]>(() => seeded.messages);
  const [convId, setConvId] = useState<string | null>(() => seeded.id);
  const [threadReady, setThreadReady] = useState(() => !!(orgCode && uid));
  const [question, setQuestion] = useState('');
  const [asking, setAsking] = useState(false);
  /* The cached briefing, exactly as ZanaDock holds it.
     Without it, askZana's local-answer path has no brief to read: the
     fleet-overview shortcut is accepted by canAnswerLocally, finds nothing,
     and answers "I couldn't pull a fresh briefing just now" with
     aiStatus:'unavailable' — WITHOUT EVER CALLING THE SERVER, even when the
     server is healthy. The dock passed `{ brief }` and this page did not, so
     the same question got two different answers depending on which surface
     the owner happened to be looking at. */
  const [brief, setBrief] = useState<any | null>(null);
  const [answerState, setAnswerState] = useState<ZanaState>('online');
  const [confirmKey, setConfirmKey] = useState<string | null>(null);
  /* The op Zana is currently holding, so a spoken "yes" can complete it.
     Mirrors ZanaDock — the two surfaces must behave as one conversation.
     2026-09-17: held with where it was asked; see HeldConfirm. */
  const pendingRef = useRef<HeldConfirm | null>(null);
  /** The last question, so a chip tapped on its answer knows whether the owner
   *  wanted a walk or simply to be taken there (2026-09-18). */
  const lastAskRef = useRef('');
  /** The thread as this surface last rendered it — lets hydrate tell its own
   *  save apart from the dock (or a notice) changing the thread underneath. */
  const chatRef = useRef<ChatMsg[]>(chat);
  chatRef.current = chat;
  /** The org open NOW, for code resuming after an await — the closure's
   *  `orgCode` is the one the work started in. See BRANCH_SWITCHED_REPLY. */
  const orgRef = useRef(orgCode);
  orgRef.current = orgCode;
  const [runningKey, setRunningKey] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState<ZanaConversation[]>([]);
  const [syncLabel, setSyncLabel] = useState<string | null>(null);
  const [coveragePct, setCoveragePct] = useState<number | null>(null);
  const [guideOffer, setGuideOffer] = useState<{
    headline: string;
    topic?: string;
    findingTitle?: string;
    findingKey?: string;
    busId?: string;
  } | null>(null);
  const guideCheckedRef = useRef<string | null>(null);
  const endRef = useRef<HTMLDivElement | null>(null);
  /* How much chrome sits above this page right now. Measured from the DOM and
     re-measured on resize, so a banner appearing cannot push the composer off
     the bottom of the viewport. 114 is only the initial guess. */
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [topOffset, setTopOffset] = useState(114);
  useEffect(() => {
    const measure = () => {
      const top = rootRef.current?.getBoundingClientRect().top;
      if (typeof top === 'number' && Number.isFinite(top)) {
        setTopOffset(Math.max(0, Math.round(top)) + 24);
      }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  });
  const hydratedRef = useRef(false);
  const skipPersistRef = useRef(false);

  const refreshHistory = useCallback(() => {
    if (!orgCode || !uid) {
      setHistory([]);
      return;
    }
    setHistory(listConversationHistory(orgCode, uid));
  }, [orgCode, uid]);

  // Restore active thread when mounting / switching org or user — and stay in
  // sync with the floating dock (same sessionStorage thread).
  useEffect(() => {
    hydratedRef.current = false;
    if (!orgCode || !uid) {
      setChat([]);
      setConvId(null);
      hydratedRef.current = true;
      setThreadReady(true);
      return;
    }
    const hydrate = () => {
      const active = loadActiveConversation(orgCode, uid);
      /* 2026-09-17: the thread moved under this page — the dock answered, a
         notice landed, the thread was swapped. Whatever this page was holding
         was asked in a conversation that has since moved on. */
      if (threadMovedUnder(chatRef.current, active?.messages || [])) {
        pendingRef.current = null;
        setConfirmKey(null);
      }
      skipPersistRef.current = true;
      setChat(active?.messages || []);
      setConvId(active?.id || null);
      setThreadReady(true);
      queueMicrotask(() => { skipPersistRef.current = false; });
    };
    hydrate();
    refreshHistory();
    hydratedRef.current = true;
    return subscribeZanaChat(hydrate);
  }, [orgCode, uid, refreshHistory]);

  // Persist on every chat change (after hydrate).
  useEffect(() => {
    if (!hydratedRef.current || !orgCode || !uid || skipPersistRef.current) return;
    const saved = saveActiveConversation(orgCode, uid, chat, convId);
    if (saved && saved.id !== convId) setConvId(saved.id);
  }, [chat, orgCode, uid, convId]);

  // 1h idle while the site stays open — archive and clear.
  useEffect(() => {
    if (!orgCode || !uid) return;
    const tick = () => {
      const active = loadActiveConversation(orgCode, uid);
      if (!active && chat.length) {
        setChat([]);
        setConvId(null);
        // The held write belonged to the thread that just went to History.
        pendingRef.current = null;
        setConfirmKey(null);
        refreshHistory();
      }
    };
    const iv = window.setInterval(tick, Math.min(60_000, ZANA_CHAT_IDLE_MS / 4));
    const onVis = () => { if (document.visibilityState === 'visible') tick(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.clearInterval(iv);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [orgCode, uid, chat.length, refreshHistory]);

  /* sessionStorage dies with the tab, so a conversation the owner simply closed
     never reached History. `pagehide` fires for close, reload and bfcache
     navigation; `visibilitychange` covers the mobile case where pagehide can be
     skipped. Archiving is idempotent, so firing twice is harmless. */
  useEffect(() => {
    if (!orgCode || !uid) return;
    const archive = () => archiveActiveConversation(orgCode, uid);
    const onHide = () => { if (document.visibilityState === 'hidden') archive(); };
    window.addEventListener('pagehide', archive);
    document.addEventListener('visibilitychange', onHide);
    return () => {
      window.removeEventListener('pagehide', archive);
      document.removeEventListener('visibilitychange', onHide);
    };
  }, [orgCode, uid]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [chat, asking]);

  const face: ZanaState = asking ? 'thinking' : chat.length ? answerState : 'online';

  /* 2026-09-17: a line owed to the thread of the org it happened in — a receipt
     for a write still running when the owner switched branches goes to the
     branch it ran in, not the one now open (as the dock). */
  const sayIn = useCallback((org: string | null, text: string) => {
    if (org && org !== orgRef.current) {
      if (uid) appendZanaNotice(org, uid, text);
      return;
    }
    setChat((c) => [...c, { from: 'zana', text }]);
  }, [uid]);

  const runIntent = useCallback(async (intent: ZanaIntent, key: string) => {
    if (!onExecuteIntent || !isExecutableIntent(intent)) return;
    const ranIn = orgRef.current;
    setConfirmKey(null);
    /* Disarm the spoken confirm too — a chip tap and a typed "yes" are two
       routes to the same write, and leaving the ref armed after one of them
       would let the other run it a second time. */
    pendingRef.current = null;
    setRunningKey(key);
    try {
      const result = await onExecuteIntent(intent);
      if (typeof result === 'string' && result) sayIn(ranIn, result);
    } catch {
      sayIn(ranIn, "That didn't go through — try again in a moment.");
    } finally {
      setRunningKey((cur) => (cur === key ? null : cur));
    }
  }, [onExecuteIntent, sayIn]);

  /* 2026-09-17: a branch switch starts from a clean Guide slate — same reason
     as the dock: the fetches below only set truthy values, so branch B kept
     branch A's headline and "Show me" walked B with A's topic. */
  useEffect(() => {
    setGuideOffer(null);
    setSyncLabel(null);
    setCoveragePct(null);
    // …and nothing held from the last branch may be confirmed in this one.
    pendingRef.current = null;
    setConfirmKey(null);
  }, [orgCode]);

  // Latest briefing — read via the backend, like the dock. Client Firestore
  // reads of aiBriefings are rules-gated and deny some org admins.
  useEffect(() => {
    if (!orgCode) return undefined;
    let alive = true;
    fetchZanaBriefing(orgCode)
      .then((b) => { if (alive) setBrief(b || null); })
      .catch(() => { if (alive) setBrief(null); });
    fetchZanaSync(orgCode).then((s) => {
      if (!alive || !s) return;
      if (s.learningLabel) setSyncLabel(s.learningLabel);
      if (typeof s.coveragePct === 'number') setCoveragePct(s.coveragePct);
    }).catch(() => {});
    return () => { alive = false; };
  }, [orgCode]);

  // Guide Mode — soft-open when Sync+Decide say she has something real.
  // Same path as the dock; full-page chat must not be a second, quieter Zana.
  // (The dock skips its own check while this page is open — see `hidden`.)
  /* 2026-09-17: screen context via a ref, the check keyed on the page and
     released if cancelled before answering — the notes on the dock's copy
     of this effect explain all three. */
  const guidePageContextRef = useRef(pageContext);
  guidePageContextRef.current = pageContext;
  const guideLiveRef = useRef(liveCounts);
  guideLiveRef.current = liveCounts;
  const guidePage = pageContext?.page || 'desk';
  useEffect(() => {
    // `takeoverActive`, not `driving`: see the dock's copy of this check.
    if (!orgCode || takeoverActive) return;
    const key = `${orgCode}:${guidePage}`;
    if (guideCheckedRef.current === key) return;
    guideCheckedRef.current = key;
    let alive = true;
    let landed = false;
    fetchZanaGuide(orgCode, { mode: 'proactive', pageContext: guidePageContextRef.current, liveCounts: guideLiveRef.current })
      .then((g) => {
        if (!alive) return;
        landed = true;
        if (!g || !g.decision) return;
        if (g.learningLabel) setSyncLabel(g.learningLabel);
        if (typeof g.coveragePct === 'number') setCoveragePct(g.coveragePct);
        if (g.decision.softOpen && g.decision.headline) {
          setGuideOffer({
            headline: g.decision.headline,
            topic: g.decision.lens?.topic,
            findingTitle: g.decision.finding?.title,
            findingKey: g.decision.finding?.key,
            busId: g.decision.finding?.busId,
          });
        } else {
          setGuideOffer(null);
        }
      })
      .catch(() => {});
    return () => {
      alive = false;
      if (!landed && guideCheckedRef.current === key) guideCheckedRef.current = null;
    };
  }, [orgCode, takeoverActive, guidePage]);

  /* 2026-09-17: every start_takeover from this page goes through here. A
     takeover owns its writes (the panel asks its own Yes), so whatever the chat
     was holding is retired the moment one is dispatched. */
  const startTakeover = useCallback((params: NonNullable<ZanaIntent['params']>) => {
    pendingRef.current = null;
    setConfirmKey(null);
    // Same rule as the dock (2026-09-18): a subject is opened, not toured,
    // unless the owner asked to be walked through it. Named scripts keep theirs.
    const mode = params.mode
      || (params.scriptId ? 'walk' : (wantsGuidedWalk(lastAskRef.current) ? 'walk' : 'jump'));
    return onExecuteIntent?.({ actionId: 'start_takeover', params: { ...params, mode } });
  }, [onExecuteIntent]);

  const send = useCallback(async (raw: string) => {
    const q = raw.trim();
    if (!q || !orgCode) return;
    lastAskRef.current = q;
    if (asking) {
      // Enter was firing all along — `send` dropped the keystroke silently
      // while a previous ask was in flight, which reads exactly like a dead
      // Enter key. Say so rather than swallowing it.
      setChat((c) => [...c, {
        from: 'zana',
        text: "Still working on the last one — give me a second and ask again.",
      }]);
      return;
    }

    /* Guide Mode interrupt — "stop" / "enough" / "later" always yields to chat. */
    if (isGuideInterrupt(q) && (takeoverActive || guideOffer)) {
      setChat((c) => [...c, { from: 'you', text: q }]);
      setQuestion('');
      // 2026-09-17: interrupt words are denial words too — disarm the held op
      // here, or a later "ok" runs what the owner just cancelled (as the dock).
      pendingRef.current = null;
      setConfirmKey(null);
      if (guideOffer) {
        setGuideOffer(null);
        void fetchZanaGuide(orgCode, { text: q, mode: 'reactive', pageContext, liveCounts });
      }
      if (takeoverActive) onStopGuide?.();
      setChat((c) => [...c, {
        from: 'zana',
        text: "Okay — I'm back in chat. Ask me anything.",
      }]);
      return;
    }

    /* Saying yes out loud — see the note in zanaAskShared. Zana suggests these
       words herself, so they have to complete the op she is holding rather
       than arriving as a fresh question that re-arms it for ever. */
    const held = pendingRef.current;
    if (held && (isAffirmation(q) || isDenial(q))) {
      setQuestion('');
      pendingRef.current = null;
      setConfirmKey(null);
      // Only while it is still the question on the table — HeldConfirm.
      if (!heldConfirmIsLive(held, { orgCode, chat, takeoverActive, now: Date.now() })) {
        setChat((c) => [...c, { from: 'you', text: q }, { from: 'zana', text: staleHeldConfirmReply(isAffirmation(q)) }]);
        return;
      }
      if (isAffirmation(q)) {
        setChat((c) => [...c, { from: 'you', text: q }]);
        await runIntent(held.intent, `spoken-${held.intent.actionId}`);
        return;
      }
      setChat((c) => [...c,
        { from: 'you', text: q },
        { from: 'zana', text: `Left it alone — nothing was ${labelForZanaIntent(held.intent).toLowerCase()}.` },
      ]);
      return;
    }
    pendingRef.current = null;
    const historyTurns: AskTurn[] = chat.slice(-6).map((m) => ({ from: m.from, text: m.text }));
    const episodes = episodesFromChat(chat, 3);
    setChat((c) => [...c, { from: 'you', text: q }]);
    setQuestion('');
    setAsking(true);
    const askedIn = orgCode;
    const res = await askZana(
      orgCode,
      q,
      pageContext,
      historyTurns,
      {
        unreadAlertCount: liveUnreadAlertCount,
        highRiskBusCount: liveHighRiskBusCount,
      },
      { brief },
      episodes,
    );
    setAsking(false);
    /* 2026-09-17: the owner switched branches while this was loading — the
       answer, its chips, its held write and its takeover / auto-read belong to
       the branch it was asked in. None of it lands here (as the dock). */
    if (orgRef.current !== askedIn) {
      if (orgRef.current) setChat((c) => [...c, { from: 'zana', text: BRANCH_SWITCHED_REPLY }]);
      return;
    }
    const shaped = shapeAskResponse(q, res, {
      unreadAlertCount: liveUnreadAlertCount,
      highRiskBusCount: liveHighRiskBusCount,
    });
    setAnswerState((shaped.mascotState as ZanaState) || 'talking');
    const answerId = newZanaMsgId();
    setChat((c) => [...c, {
      from: 'zana',
      id: answerId,
      orgCode: askedIn,
      text: shaped.text,
      intents: shaped.intents,
      offer: shaped.offer,
      source: (res.source as ZanaMsgSource) || undefined,
      // The dock has always shown this; the full-screen page — the bigger
      // surface, and the one built for decisions — never told anyone why the
      // answers had gone shallow or when depth came back.
      restingLimit: shaped.aiStatus === 'limited' && shaped.limit ? shaped.limit : undefined,
    }]);
    // Hold the op she just offered, so "yes" above can complete it.
    pendingRef.current = armHeldConfirm(shaped.intents, shaped.text, { orgCode, msgId: answerId, now: Date.now() });
    if (shaped.offer && orgCode) void warmZanaContext(orgCode);
    // Explicit handover — same as the dock: the ask WAS the consent.
    if (shaped.autoScriptId && onExecuteIntent) {
      void startTakeover({
        scriptId: shaped.autoScriptId,
        ...(shaped.autoOps ? { ops: shaped.autoOps } : {}),
        ...(shaped.autoSkipChooser ? { skipChooser: true } : {}),
        ...(shaped.autoUnattended ? { auto: true } : {}),
      });
    } else if (shaped.autoTopic && onExecuteIntent) {
      void startTakeover({
        topic: shaped.autoTopic,
        ...(shaped.autoSkipChooser ? { skipChooser: true } : {}),
        ...(shaped.autoUnattended ? { auto: true } : {}),
      });
    } else if (shaped.autoRead && onExecuteIntent) {
      void runIntent(shaped.autoRead, `auto-${shaped.autoRead.actionId}`);
    }
  }, [asking, chat, orgCode, pageContext, liveUnreadAlertCount, liveHighRiskBusCount, onExecuteIntent, runIntent, brief, takeoverActive, guideOffer, onStopGuide, startTakeover]);

  const handleNewConversation = useCallback(() => {
    if (!orgCode || !uid) {
      setChat([]);
      setConvId(null);
      return;
    }
    // A pending op belongs to the thread that offered it. Carrying it into a
    // brand-new conversation would let the first "yes" of a fresh chat execute
    // a write proposed in the last one.
    pendingRef.current = null;
    startNewConversation(orgCode, uid);
    setChat([]);
    setConvId(null);
    setConfirmKey(null);
    setHistoryOpen(false);
    refreshHistory();
  }, [orgCode, uid, refreshHistory]);

  const handleOpenHistory = useCallback(() => {
    refreshHistory();
    setHistoryOpen((v) => !v);
  }, [refreshHistory]);

  const handleRestore = useCallback((id: string) => {
    if (!orgCode || !uid) return;
    const restored = restoreConversation(orgCode, uid, id);
    if (!restored) return;
    setChat(restored.messages || []);
    setConvId(restored.id);
    setConfirmKey(null);
    // Restoring an old thread must not re-arm whatever it was holding.
    pendingRef.current = null;
    setHistoryOpen(false);
    refreshHistory();
  }, [orgCode, uid, refreshHistory]);

  const handleDeleteHistory = useCallback((id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!orgCode || !uid) return;
    deleteHistoryConversation(orgCode, uid, id);
    refreshHistory();
  }, [orgCode, uid, refreshHistory]);

  const screenLine = useMemo(() => {
    const cards = pageContext?.cards || [];
    if (!cards.length) return 'She can see this screen while you talk.';
    return `She can see this screen: ${cards.map((c) => `${c.label} ${c.value}`).join(' · ')}.`;
  }, [pageContext]);

  if (!orgCode) return null;

  return (
    /* A BOUNDED height, not a minimum.
       Nothing in the chain from `.pear-shell` down established a definite
       height, so the transcript's `overflowY: auto` was dead code: in a column
       flex container whose own height is only a minimum, a `flex: 1` child
       with the default `min-height: auto` never shrinks below its content. The
       transcript grew, the card grew, the page grew, and a long conversation
       scrolled the whole DOCUMENT — pushing the composer off the bottom (the
       "chat input renders off-screen") and leaving the page trailing into
       empty gradient (the "giant white void"). Both symptoms are the same bug,
       one vertical and one at the end of the scroll.
       `minHeight: 0` on every flex child is the other half: without it the
       shrink still cannot happen. */
    <div
      ref={rootRef}
      style={{
        padding: '18px 24px 28px',
        display: 'flex',
        flexDirection: 'column',
        height: `calc(100vh - ${topOffset}px)`,
        minHeight: 0,
      }}
    >
      <style>{ZANA_CHAT_STYLES}</style>
      <div
        style={{
          flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0,
          borderRadius: 20, border: `1px solid ${EDGE}`, overflow: 'hidden',
          background: 'linear-gradient(160deg,#241a45 0%,#1a1430 58%,#160f28 100%)',
          boxShadow: '0 18px 48px rgba(23,12,54,.35)',
        }}
      >
        {/* ── Header ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 20px', borderBottom: `1px solid ${EDGE}`, position: 'relative' }}>
          <Zana state={face} size={54} animated />
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#f3efff', letterSpacing: '-.2px' }}>
              Zana{asking ? ' is thinking…' : ''}
            </div>
            <div style={{ fontSize: 12, color: '#b9a9e6', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {guideOffer
                ? guideOffer.headline
                : (syncLabel
                  ? `${syncLabel}${coveragePct != null ? ` · ${coveragePct}% of your fleet mapped so far` : ''}`
                  : screenLine)}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            {uid && (
              <button
                type="button"
                onClick={handleOpenHistory}
                style={{
                  border: `1px solid ${EDGE}`, background: historyOpen ? 'rgba(155,124,255,.22)' : 'transparent',
                  color: '#c9c0e8', borderRadius: 999, padding: '6px 13px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                }}
              >
                History{history.length ? ` (${history.length})` : ''}
              </button>
            )}
            {chat.length > 0 && (
              <button
                type="button"
                onClick={handleNewConversation}
                style={{
                  border: `1px solid ${EDGE}`, background: 'transparent',
                  color: '#c9c0e8', borderRadius: 999, padding: '6px 13px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                }}
              >
                New conversation
              </button>
            )}
          </div>

          {historyOpen && (
            <div
              className="zana-scroll-hide"
              style={{
                position: 'absolute', top: '100%', right: 16, zIndex: 20, marginTop: 6,
                width: 340, maxWidth: 'min(340px, calc(100% - 32px))', maxHeight: 320, overflowY: 'auto',
                borderRadius: 14, border: `1px solid ${EDGE}`, background: '#1e1738',
                boxShadow: '0 16px 40px rgba(0,0,0,.45)',
              }}
            >
              <div style={{ padding: '10px 14px', fontSize: 11, fontWeight: 800, letterSpacing: '.06em', color: '#a898db', textTransform: 'uppercase', borderBottom: `1px solid ${EDGE}` }}>
                Past conversations
              </div>
              {history.length === 0 ? (
                <div style={{ padding: '16px 14px', fontSize: 13, color: '#b9a9e6', lineHeight: 1.5 }}>
                  Nothing archived yet. Tap New conversation to save the current thread here.
                </div>
              ) : (
                history.map((h) => (
                  <div
                    key={h.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleRestore(h.id)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleRestore(h.id); }}
                    style={{
                      display: 'flex', alignItems: 'flex-start', gap: 8, padding: '11px 14px',
                      borderBottom: `1px solid rgba(155,124,255,.12)`, cursor: 'pointer',
                      background: h.id === convId ? 'rgba(155,124,255,.14)' : 'transparent',
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#efe9ff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {h.title}
                      </div>
                      <div style={{ fontSize: 11, color: '#a898db', marginTop: 3 }}>
                        {formatWhen(h.updatedAt)} · {h.messages.length} messages
                      </div>
                    </div>
                    <button
                      type="button"
                      title="Delete"
                      onClick={(e) => handleDeleteHistory(h.id, e)}
                      style={{
                        border: 'none', background: 'transparent', color: '#8a7bb0',
                        fontSize: 16, lineHeight: 1, cursor: 'pointer', padding: '2px 4px',
                      }}
                    >
                      ×
                    </button>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* ── Transcript ── */}
        <ZanaContextBar
          turnCount={chat.length}
          historyCount={history.length}
          onOpenHistory={uid ? handleOpenHistory : undefined}
        />
        <div className="zana-scroll-hide" style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '12px 20px 8px' }}>
          {guideOffer && (
            <div style={{
              maxWidth: 760, margin: '0 auto 14px', padding: '12px 14px', borderRadius: 14,
              background: 'rgba(155,124,255,.14)', border: '1px solid rgba(155,124,255,.35)',
            }}>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.08em', color: '#c4b3ff', marginBottom: 4 }}>
                GUIDE MODE
              </div>
              <div style={{ fontSize: 14, lineHeight: 1.45, color: '#eee9fb', marginBottom: 12 }}>
                {guideOffer.headline}
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={() => {
                    const offer = guideOffer;
                    setGuideOffer(null);
                    void (async () => {
                      const askedIn = orgCode;
                      const g = orgCode
                        ? await fetchZanaGuide(orgCode, { text: 'show me', mode: 'reactive', pageContext, liveCounts })
                        : null;
                      // 2026-09-17: a switch while it loaded — not A's walk in B.
                      if (orgRef.current !== askedIn) return;
                      const topic = g?.decision?.lens?.topic || offer?.topic || 'fleet';
                      const findingTitle = g?.decision?.finding?.title || offer?.findingTitle;
                      const busId = g?.decision?.finding?.busId || offer?.busId;
                      setChat((c) => [...c, {
                        from: 'zana',
                        text: findingTitle
                          ? `I'll walk you through ${findingTitle}. Stop anytime and I'll stay in chat.`
                          : "I'll walk you through what matters on this screen. Stop anytime and I'll stay in chat.",
                      }]);
                      // The bus rides in `ops` — see the dock's "Show me".
                      void startTakeover({
                        topic,
                        skipChooser: true,
                        ...(busId ? { ops: { busId, plate: busId } } : {}),
                      });
                    })();
                  }}
                  style={{
                    background: 'rgba(155,124,255,.45)', border: 'none', color: '#fff',
                    borderRadius: 8, padding: '7px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                  }}
                >
                  Show me
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setGuideOffer(null);
                    if (orgCode) void fetchZanaGuide(orgCode, { text: 'later', mode: 'reactive', pageContext, liveCounts });
                    setChat((c) => [...c, {
                      from: 'zana',
                      text: 'Okay — I will stay quiet. Ask me anytime.',
                    }]);
                  }}
                  style={{
                    background: 'transparent', border: '1px solid rgba(255,255,255,.2)', color: '#a99fce',
                    borderRadius: 8, padding: '7px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                  }}
                >
                  Later
                </button>
              </div>
            </div>
          )}
          {threadReady && chat.length === 0 && !guideOffer && (
            <div style={{ maxWidth: 760, margin: '18px auto 0' }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#f3efff', textAlign: 'center' }}>
                Ask me the questions you would ask an analyst.
              </div>
              <div style={{ fontSize: 13.5, color: '#b9a9e6', textAlign: 'center', marginTop: 8, lineHeight: 1.6 }}>
                I read your fleet&apos;s own data — fuel, routes, drivers, alerts, money — and I remember what you
                tell me. Counts and lookups I answer instantly; decisions I actually think about.
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 10, marginTop: 22 }}>
                {STARTERS.map((s) => (
                  <button
                    key={s.label}
                    type="button"
                    onClick={() => void send(s.q)}
                    style={{
                      textAlign: 'left', border: `1px solid ${EDGE}`, background: PANEL_SOFT,
                      borderRadius: 14, padding: '13px 15px', cursor: 'pointer', color: '#efe9ff',
                    }}
                  >
                    <div style={{ fontSize: 13.5, fontWeight: 800 }}>{s.label}</div>
                    <div style={{ fontSize: 11.5, color: '#a898db', marginTop: 4, lineHeight: 1.45 }}>{s.q}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {chat.map((m, i) => (
            <div key={`${i}-${m.from}`} style={{ maxWidth: 820, margin: '0 auto 16px', display: 'flex', justifyContent: m.from === 'you' ? 'flex-end' : 'flex-start' }}>
              {/* The 86% cap lives HERE, not on the bubble. This div is a flex
                  item sized to its content, so a percentage set inside it
                  resolved against the text's own width and left every message
                  a different distance from the right edge. Against the row —
                  a definite 820px — 86% is the same 705px for every message,
                  which is what makes the right edges line up. */}
              <div style={{ maxWidth: '86%', minWidth: 0 }}>
                <ZanaMessageBubble from={m.from} text={m.text} source={m.source} />
                {m.from === 'zana' && m.restingLimit && (() => {
                  const footer = restingFooter(m.restingLimit, formatResetAt);
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6, marginTop: 7, fontSize: 11, color: '#c9a97a' }}>
                      <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#f0a34a' }} />
                      <span style={{ fontWeight: 700 }}>{footer.headline}</span>
                    </div>
                  );
                })()}
                {m.from === 'zana' && !!m.intents?.length && onExecuteIntent && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginTop: 9 }}>
                    {m.intents.filter(isExecutableIntent).map((intent, j) => {
                      const key = `${i}-${j}`;
                      const needsConfirm = intentNeedsConfirm(intent);
                      const running = runningKey === key;
                      // 2026-09-17: the checks a chip still owes — see chipMayRun.
                      const mayRun = chipMayRun(m, needsConfirm, { orgCode, takeoverActive });
                      const blockedWhy = mayRun ? undefined
                        : takeoverActive && needsConfirm ? 'Finish or stop the walkthrough first'
                          : 'Asked in another branch — ask me again here';
                      if (confirmKey === key) {
                        return (
                          <span key={key} style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                            <button
                              type="button"
                              disabled={!mayRun}
                              title={blockedWhy}
                              onClick={() => { if (mayRun) void runIntent(intent, key); }}
                              style={{
                                border: '1px solid rgba(255,180,84,.65)', background: 'rgba(255,159,28,.25)',
                                color: '#ffe9cf', borderRadius: 999, padding: '6px 12px', fontSize: 12, fontWeight: 800,
                                cursor: mayRun ? 'pointer' : 'not-allowed', opacity: mayRun ? 1 : 0.45,
                              }}
                            >
                              Confirm: {labelForZanaIntent(intent).toLowerCase()} ✓
                            </button>
                            <button
                              type="button"
                              onClick={() => setConfirmKey(null)}
                              style={{
                                border: `1px solid ${EDGE}`, background: 'transparent', color: '#c9c0e8',
                                borderRadius: 999, padding: '6px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                              }}
                            >
                              Cancel
                            </button>
                          </span>
                        );
                      }
                      return (
                        <button
                          key={key}
                          type="button"
                          disabled={running || !mayRun}
                          title={blockedWhy}
                          onClick={() => {
                            if (!mayRun) return;
                            if (needsConfirm) setConfirmKey(key);
                            else void runIntent(intent, key);
                          }}
                          style={{
                            border: needsConfirm ? '1px solid rgba(255,180,84,.5)' : `1px solid ${EDGE}`,
                            background: needsConfirm ? 'rgba(255,159,28,.14)' : 'rgba(155,124,255,.18)',
                            color: needsConfirm ? '#ffe9cf' : '#efe9ff',
                            borderRadius: 999, padding: '6px 12px', fontSize: 12, fontWeight: 700,
                            cursor: running ? 'wait' : mayRun ? 'pointer' : 'not-allowed',
                            opacity: running ? 0.6 : mayRun ? 1 : 0.45,
                          }}
                        >
                          {running ? 'Working…' : `${labelForZanaIntent(intent)} ${needsConfirm ? '⚠' : '→'}`}
                        </button>
                      );
                    })}
                  </div>
                )}
                {m.from === 'zana' && m.offer && onExecuteIntent && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 12.5, color: '#c4b3ff', marginBottom: 7, lineHeight: 1.45 }}>{m.offer.prompt}</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 420 }}>
                      <button
                        type="button"
                        onClick={() => {
                          setChat((prev) => prev.map((row, j) => (
                            j === i ? { ...row, offer: undefined } : row
                          )));
                          setChat((prev) => [...prev, {
                            from: 'zana',
                            text: 'Got it — staying in chat. Ask anything else whenever you are ready.',
                          }]);
                        }}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                          width: '100%', border: `1px solid ${EDGE}`, borderRadius: 12,
                          padding: '11px 14px', background: 'rgba(255,255,255,.06)',
                          color: '#efe9ff', fontSize: 13.5, fontWeight: 700, cursor: 'pointer',
                        }}
                      >
                        {m.offer.explainLabel || 'Explain it in chat'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setChat((prev) => prev.map((row, j) => (
                            j === i ? { ...row, offer: undefined } : row
                          )));
                          void startTakeover({ topic: m.offer!.topic });
                        }}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                          width: '100%', border: 'none', borderRadius: 12, padding: '12px 14px',
                          background: m.offer.kind === 'do'
                            ? 'linear-gradient(120deg,#7a4bff,#a06bff)'
                            : 'linear-gradient(120deg,#4b3aa8,#6f4ff0)',
                          color: '#fff', fontSize: 14, fontWeight: 800, cursor: 'pointer',
                          boxShadow: '0 10px 24px -12px rgba(120,80,255,.9)',
                        }}
                      >
                        <span aria-hidden>{m.offer.kind === 'do' ? '⚡' : '👉'}</span>
                        {m.offer.label}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* A long thread, said in words a person uses.
              Zana carries the last few turns of a conversation, not all of it,
              so a thread that has run on starts losing its own beginning — she
              answers the last question well and forgets the shape of the one
              before. Rather than let that happen quietly, she says so and
              offers a fresh start.

              Deliberately NOT "context window is full" here: Gabriel's rule
              (2026-08-20) is that a school administrator in Nairobi has no
              reason to know what a context window is, and copy that assumes
              they do is copy that fails. What she keeps IS kept — her notes on
              the fleet and what this person has asked before both outlive the
              conversation — so the offer is honest. */}
          {chat.length >= 24 && !asking && (
            <div style={{
              maxWidth: 820,
              margin: '0 auto 16px',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              flexWrap: 'wrap',
              padding: '10px 14px',
              borderRadius: 12,
              border: `1px solid ${EDGE}`,
              background: 'rgba(255,255,255,.03)',
              fontSize: 12.5,
              color: '#c9c0e8',
            }}>
              <span>
                We&rsquo;ve covered a lot in this one. Starting a fresh chat keeps my
                answers sharp — I&rsquo;ll still remember what matters about your fleet.
              </span>
              <button
                type="button"
                onClick={handleNewConversation}
                style={{
                  marginLeft: 'auto',
                  border: `1px solid ${EDGE}`,
                  background: 'transparent',
                  color: '#c4b3ff',
                  borderRadius: 999,
                  padding: '6px 14px',
                  fontSize: 12,
                  fontWeight: 800,
                  cursor: 'pointer',
                }}
              >
                Start a fresh chat
              </button>
            </div>
          )}

          {asking && (
            <div style={{ maxWidth: 820, margin: '0 auto 16px' }}>
              <ZanaThinkingBubble label="Reading your fleet…" />
            </div>
          )}
          <div ref={endRef} />
        </div>

        {/* ── Composer ── */}
        <div style={{ borderTop: `1px solid ${EDGE}`, padding: '14px 20px 16px', background: PANEL }}>
          <div style={{ maxWidth: 820, margin: '0 auto', display: 'flex', gap: 10, alignItems: 'flex-end' }}>
            <textarea
              className="zana-scroll-hide"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  void send(question);
                }
              }}
              rows={1}
              placeholder="Ask about your fleet — Enter to send, Shift+Enter for a new line"
              style={{
                flex: 1, resize: 'none', maxHeight: 140, minHeight: 46, overflowY: 'auto',
                borderRadius: 14, border: `1px solid ${EDGE}`, background: PANEL_SOFT,
                color: '#f3efff', padding: '13px 15px', fontSize: 14, font: 'inherit', fontFamily: 'inherit',
                outline: 'none',
              }}
            />
            <button
              type="button"
              disabled={asking || !question.trim()}
              onClick={() => void send(question)}
              style={{
                border: 'none', borderRadius: 14, padding: '13px 22px', fontSize: 14, fontWeight: 800,
                background: asking || !question.trim() ? 'rgba(155,124,255,.3)' : '#9b7cff',
                color: asking || !question.trim() ? '#c9c0e8' : '#150f26',
                cursor: asking || !question.trim() ? 'not-allowed' : 'pointer',
              }}
            >
              Ask
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
